'use strict';

define('vbsw/private/offlineManager',['vbsw/private/utils', 'vbc/private/log'],
  (Utils, Log) => {
    const logger = Log.getLogger('/vbsw/private/fetchHandler');

    /**
     * Manages offline functionality for the application.
     */
    class OfflineManager {
      /**
       * Creates an instance of OfflineManager.
       *
       * @param {string} scope - The scope of the service worker.
       * @param {Object} config - Configuration for the OfflineManager.
       */
      constructor(scope, config) {
        this.scope = scope;
        this.config = config;

        this.offlineHandler = null;
        this.secondaryOfflineHandler = null;

        /**
         * Mapping between a request and its associated info such as offline handlers.
         * @type {Map}
         */
        this.requestInfoMap = new Map();

        requirejs.config({
          map: {
            '*': {
              opt: 'persist', // for backwards compatibility with opt prefix
            },
          },
        });
      }

      /**
       * Initializes the OfflineManager.
       *
       * @returns {Promise<void>} A promise that resolves when initialization is complete.
       */
      initialize() {
        this.initializePromise = this.initializePromise
          || this.initializePersistenceManager()
            .then((pm) => pm.register({ scope: '/' })
              .then((registration) => {
                // intercept fetch calls
                registration.addEventListener('fetch', (event) => {
                  const { request } = event;

                  // save the offline handlers associated with the request so it can be looked up when chaining
                  // offline handlers
                  const requestInfo = {
                    offlineHandlerStack: this.getOfflineHandlers(),
                  };
                  this.saveRequestInfo(request, requestInfo);

                  event.respondWith(this.handleRequest(request)
                    .finally(() => {
                      // delete the info associated with the request
                      this.deleteRequestInfo(request);
                    }));
                });
              }));

        return this.initializePromise;
      }

      /**
       * Initializes the PersistenceManager without registering a default persistence store.
       *
       * @returns {Promise<*>}
       */
      initializePersistenceManager() {
        this.persistenceManagerPromise = this.persistenceManagerPromise
          || Utils.getResources(['persist/persistenceManager'])
            .then((modules) => {
              const [persistenceManager] = modules;

              return persistenceManager.init().then(() => {
                this.persistenceManager = persistenceManager;

                this.setupBrowserFetch(persistenceManager);

                return persistenceManager;
              });
            });

        return this.persistenceManagerPromise;
      }

      /**
       * Sets up browserFetch to either point to browserFetch from the persistence manager or the actual browser fetch.
       *
       * @param {*} persistenceManager - The persistence manager instance.
       */
      setupBrowserFetch(persistenceManager) {
        const pm = persistenceManager;

        if (pm) {
          this.browserFetch = pm.browserFetch.bind(pm);

          // override the pm browserFetch to call back to OfflineManager.handleRequest
          pm.browserFetch = (request) => this.handleRequest(request);
        } else {
          // service worker won't let you directly point to fetch so use a wrapper function instead
          this.browserFetch = (request) => fetch(request);
        }
      }

      /**
       * Handles a request by either handing it off to an offline handler or the network.
       *
       * @param {Request} request the request to be handled by the offline handler or sent to the network
       */
      handleRequest(request) {
        return Promise.resolve().then(() => {
          // look up the associated info for the request
          const { offlineHandlerStack = [] } = this.getRequestInfo(request) || {};

          // call browserFetch if the offline handler stack is empty
          if (offlineHandlerStack.length === 0) {
            return this.browserFetch(request);
          }

          // hand off to the offline handler
          const offlineHandler = offlineHandlerStack.pop();
          return offlineHandler.handleRequest(request, this.scope);
        });
      }

      /**
       * Remembers the given info associated with a request
       *
       * @param {Request} request the request
       * @param {Object} info info associated with the request
       * @private
       */
      saveRequestInfo(request, info) {
        this.requestInfoMap.set(request, info);
      }

      /**
       * Returns the info associated with the given request.
       *
       * @param {Request} request the request for looking up the associated info
       * @returns {Object}
       * @private
       */
      getRequestInfo(request) {
        return this.requestInfoMap.get(request);
      }

      /**
       * Delete the info associated with the given request.
       *
       * @param {Request} request the request for the info to delete
       * @private
       */
      deleteRequestInfo(request) {
        this.requestInfoMap.delete(request);
      }

      /**
       * Return the default URL for the offline handler.
       *
       * @returns {string} The default offline handler URL.
       */
      // eslint-disable-next-line class-methods-use-this
      getDefaultOfflineHandlerUrl() {
        // get the base token url from vbInitConfig if any
        const vbConfig = window.vbInitConfig || {};

        if (vbConfig.CONTEXT_OFFLINE_HANDLER_PATH) {
          return vbConfig.CONTEXT_OFFLINE_HANDLER_PATH;
        }

        // requirejs baseUrl is already set in main.js using the BASE_URL and
        // the BASE_URL_TOKEN so we don't need to do any more path manipulation
        // to access app-flow.js. It is important to NOT terminate the file with
        // .js otherwise requirejs does not conform to the "baseUrl + paths"
        // rules for finding it.
        // https://requirejs.org/docs/api.html#jsfiles
        return 'app-flow';
      }

      /**
       * Returns an array of all the registered offline handlers.
       *
       * @returns {Array}
       */
      getOfflineHandlers() {
        const offlineHandlers = [];

        // push the secondary offline handler on to the stack first if exist
        if (this.secondaryOfflineHandler) {
          offlineHandlers.push(this.secondaryOfflineHandler);
        }

        // push the primary offline handler onto the stack if exist
        if (this.offlineHandler) {
          offlineHandlers.push(this.offlineHandler);
        }

        return offlineHandlers;
      }

      /**
       * Loads and installs the offline handler class from the given url.
       *
       * @param {string} handlerUrl the url for the offline handler class
       * @returns {Promise<void>}
       */
      installOfflineHandler(handlerUrl = this.getDefaultOfflineHandlerUrl()) {
        return Promise.resolve().then(() => {
          if (handlerUrl) {
            return Utils.getResource(handlerUrl).then((Module) => {
              let module;
              if (Module) {
                // Module can be either a constructor or a singleton
                module = typeof Module === 'function' ? new Module() : Module;
              } else {
                module = {};
              }

              if (typeof module.createOfflineHandler === 'function') {
                // make sure we initialize the PersistenceManager first
                return this.initialize()
                  .then(() => this.initializePersistenceManagerWithStore(module))
                  .then(() => module.createOfflineHandler()
                    .then((handler) => {
                      this.offlineHandler = handler;
                    }));
              }

              return undefined;
            }).catch((err) => {
              logger.info(`Failed to load offline handler from ${handlerUrl}: ${err}`);
              this.offlineHandler = null;
            });
          }

          return undefined;
        });
      }

      /**
       * Initializes the PersistenceManager and register a default persistence store.
       *
       * @param {*} offlineModule module containing offline related code
       * @returns {Promise<*>}
       */
      initializePersistenceManagerWithStore(offlineModule) {
        return this.initializePersistenceManager()
          .then((pm) => OfflineManager.registerDefaultPersistenceStoreFactory(offlineModule)
            .then(() => pm));
      }

      /**
       * Called by unit tests to uninstall offline handlers and reset persistence manager
       *
       * @returns {Promise<void>} A promise that resolves when the operation is complete.
       */
      uninstallOfflineHandlers() {
        return Promise.resolve().then(() => {
          this.offlineHandler = null;
          this.secondaryOfflineHandler = null;

          if (this.persistenceManager) {
            this.persistenceManager.browserFetch = this.browserFetch;
            this.persistenceManager = null;
            this.initializePromise = null;
          }
        });
      }

      /**
       * Uninstalls the offline handler.
       *
       * @return {Promise<void>}
       */
      uninstallOfflineHandler() {
        return Promise.resolve().then(() => {
          this.offlineHandler = null;
        });
      }

      /**
       * Installs a secondary offline handler defined in an app UI.
       *
       * @param {string} handlerUrl the url from which to load the offline handler
       * @returns {Promise<void>}
       */
      installSecondaryOfflineHandler(handlerUrl) {
        return Promise.resolve().then(() => {
          if (handlerUrl) {
            return Utils.getResource(handlerUrl).then((Module) => {
              let module;
              if (Module) {
                // Module can be either a constructor or a singleton
                module = typeof Module === 'function' ? new Module() : Module;
              } else {
                module = {};
              }

              if (typeof module.createOfflineHandler === 'function') {
                return this.initialize()
                  .then(() => module.createOfflineHandler()
                    .then((handler) => {
                      this.secondaryOfflineHandler = handler;
                    }));
              }

              return undefined;
            }).catch((err) => {
              logger.info(`Failed to load offline handler from ${handlerUrl}: ${err}`);
            });
          }

          return undefined;
        });
      }

      /**
       * Uninstalls the secondary offline handler.
       *
       * @returns {Promise<void>}
       */
      uninstallSecondaryOfflineHandler() {
        return Promise.resolve().then(() => {
          this.secondaryOfflineHandler = null;
        });
      }

      /**
       * Returns a promise that resolves to true if the PersistenceManager is online and false otherwise.
       *
       * @returns {Promise<boolean>}
       */
      isOnline() {
        return this.initializePersistenceManager().then((pm) => pm.isOnline());
      }

      /**
       * If the flag is true, force the PersistenceManager offline and vice versa.
       *
       * @param {boolean} flag if true, force the PersistenceManager offline and vice versa.
       * @returns {Promise<void>}
       */
      forceOffline(flag) {
        return this.initializePersistenceManager().then((pm) => pm.forceOffline(flag));
      }

      /**
       * Dynamically loads the PersistenceSyncManager.
       *
       * @returns {Promise<*>}
       */
      getPersistenceSyncManager() {
        if (!this.persistSyncMgrPromise) {
          this.persistSyncMgrPromise = Utils.getResource('persist/impl/PersistenceSyncManager')
            .then((PersistenceSyncManager) => new PersistenceSyncManager());
        }
        return this.persistSyncMgrPromise;
      }

      /**
       * Dynamically loads the PersistenceUtils.
       *
       * @returns {Promise<*>}
       */
      getPersistenceUtils() {
        if (!this.persistUtilsPromise) {
          this.persistUtilsPromise = Utils.getResource('persist/persistenceUtils');
        }
        return this.persistUtilsPromise;
      }

      /**
       * Deserializes the given request JSON object into a Request instance.
       *
       * @param {Object} requestJson request json to deserialize
       * @returns {Promise<Request>}
       */
      deserializeRequest(requestJson) {
        return Promise.resolve().then(() => {
          if (Utils.isWorkerThread()) {
            return this.getPersistenceUtils()
              .then((PersistenceUtils) => PersistenceUtils.requestFromJSON(requestJson));
          }

          return requestJson;
        });
      }

      /**
       * If running on the service worker thread, serialize the given result from a sync manager operation by
       * serializing any Request or Response instance into a JSON object so it can be returned to the main thread
       * via postMessage.
       *
       * @param {Object} result
       * @returns {Promise<Object>}
       */
      serializeSyncOperationResult(result) {
        return Promise.resolve().then(() => {
          if (result && Utils.isWorkerThread()) {
            return this.getPersistenceUtils().then((PersistenceUtils) => {
              if (Array.isArray(result)) {
                const serializedResult = [];
                const promises = result.map((entry) => this.serializeSyncOperationResult(entry)
                  .then((serializedEntry) => {
                    serializedResult.push(serializedEntry);
                  }));
                return Promise.all(promises).then(() => serializedResult);
              }

              if (result instanceof Request) {
                return PersistenceUtils.requestToJSON(result);
              }

              if (result instanceof Response) {
                return PersistenceUtils.responseToJSON(result);
              }

              if (Utils.isObject(result)) {
                const serializedResult = {};
                const promises = Object.keys(result)
                  .map((key) => this.serializeSyncOperationResult(result[key])
                    .then((serializedProp) => {
                      serializedResult[key] = serializedProp;
                    }));

                return Promise.all(promises).then(() => serializedResult);
              }

              if (typeof result === 'function') {
                return null;
              }

              return result;
            });
          }

          return result;
        });
      }

      /**
       * Invokes PersistenceSyncManager.sync.
       *
       * @param {Object} options - Options to be passed to the persistence sync manager.
       * @param {Object} client - The originating client of this sync call.
       * @returns {Promise<Object>} A promise that resolves with the result of the sync operation.
       */
      // eslint-disable-next-line no-unused-vars
      syncOfflineData(options, client) {
        if (this.persistenceManagerPromise) {
          return this.persistenceManagerPromise.then((pm) => Utils.getResource('persist/impl/PersistenceSyncManager')
            .then((PersistenceSyncManager) => {
              // create a PersistenceSyncManager that use our FetchHandler to perform browser fetch
              const syncMgr = new PersistenceSyncManager(
                pm.isOnline.bind(pm),
                pm.browserFetch.bind(pm),
                pm.getCache.bind(pm),
              );

              this.getOfflineHandlers().forEach((offlineHandler) => {
                if (typeof offlineHandler.beforeSyncRequestListener === 'function') {
                  syncMgr.addEventListener('beforeSyncRequest',
                    offlineHandler.beforeSyncRequestListener.bind(offlineHandler));
                }

                if (typeof offlineHandler.afterSyncRequestListener === 'function') {
                  syncMgr.addEventListener('syncRequest',
                    offlineHandler.afterSyncRequestListener.bind(offlineHandler));
                }
              });

              return syncMgr.sync(options)
                .catch((err) => this.serializeSyncOperationResult(err)
                  .then((serializedErr) => {
                    throw serializedErr;
                  }));
            }));
        }

        return Promise.reject(new Error('Cannot invoke sync before the PersistenceManager is initialized.'));
      }

      /**
       * Invokes PersistenceSyncManager.getSyncLog.
       *
       * @returns {Promise<void>}
       */
      getOfflineSyncLog() {
        return this.getPersistenceSyncManager()
          .then((syncMgr) => syncMgr.getSyncLog())
          .then((syncLog) => this.serializeSyncOperationResult(syncLog));
      }

      /**
       * Invoke PersistenceSyncManager.insertRequest.
       *
       * @param {Object} requestData
       * @param {Object} options
       * @returns {Promise<void>}
       */
      insertOfflineSyncRequest(requestData, options) {
        return this.deserializeRequest(requestData)
          .then((request) => this.getPersistenceSyncManager()
            .then((syncMgr) => syncMgr.insertRequest(request, options))
            .then((result) => this.serializeSyncOperationResult(result)));
      }

      /**
       * Invoke PersistenceSyncManager.removeRequest.
       *
       * @param {string} requestId
       * @returns {Promise<void>}
       */
      removeOfflineSyncRequest(requestId) {
        return this.getPersistenceSyncManager()
          .then((syncMgr) => syncMgr.removeRequest(requestId))
          .then((result) => this.serializeSyncOperationResult(result));
      }

      /**
       * Invoke PersistenceSyncManager.updateRequest.
       *
       * @param {string} requestId
       * @param {Object} requestData
       * @returns {Promise<Object>}
       */
      updateOfflineSyncRequest(requestId, requestData) {
        return this.deserializeRequest(requestData)
          .then((request) => this.getPersistenceSyncManager()
            .then((syncMgr) => syncMgr.updateRequest(requestId, request))
            .then((result) => this.serializeSyncOperationResult(result)));
      }

      /**
       * Sets options on the offline handler.
       *
       * @param {Object} options options to be set on the offline handler
       * @returns {Promise<void>}
       */
      setOfflineHandlerOptions(options) {
        return Promise.resolve().then(() => {
          if (this.offlineHandler && typeof this.offlineHandler.setOptions === 'function') {
            return this.offlineHandler.setOptions(options);
          }

          return undefined;
        });
      }

      /**
       * Sets options on the secondary offline handler.
       *
       * @param {Object} options options to be set on the secondary offline handler
       * @returns {Promise<void>}
       */
      setSecondaryOfflineHandlerOptions(options) {
        return Promise.resolve().then(() => {
          if (this.secondaryOfflineHandler && typeof this.secondaryOfflineHandler.setOptions === 'function') {
            return this.secondaryOfflineHandler.setOptions(options);
          }

          return undefined;
        });
      }

      /**
       * Registers the default persistence store factory. This method will first call getDefaultPersistenceFactory
       * method on the module for the offline handler if it exists. Otherwise, it defaults to registering
       * 'persist/pouchDBPersistenceStoreFactory'.
       *
       * @param {*} offlineModule module containing offline related code
       * @returns {Promise<*>}
       */
      static registerDefaultPersistenceStoreFactory(offlineModule) {
        return Promise.resolve()
          .then(() => {
            if (offlineModule && typeof offlineModule.getDefaultPersistenceStoreFactory === 'function') {
              return offlineModule.getDefaultPersistenceStoreFactory();
            }
            return null;
          })
          .catch((err) => {
            // log the error
            logger.error(err);
            return null;
          })
          .then((storeFactoryOverride) => storeFactoryOverride
            || Utils.getResource('persist/pouchDBPersistenceStoreFactory'))
          .then((storeFactory) => Utils.getResource('persist/persistenceStoreManager')
            .then((PersistenceStoreManager) => {
              PersistenceStoreManager.registerDefaultStoreFactory(storeFactory);
            }));
      }
    }

    return OfflineManager;
  });

